/**
 * Regalo automatico del carrito THCC.
 *
 * Mantiene en el carrito la(s) gomita(s) CBD de regalo segun lo que el cliente lleva:
 *   - base < meta 1  -> 0 regalos
 *   - meta 1 <= base < meta 2 -> `cantidadUno` regalos (1)
 *   - base >= meta 2 -> `cantidadDos` regalos (2)
 * Cantidad de la linea de regalo = max(0, regalos deseados - gomitas propias), donde gomitas propias son las unidades
 * de la variante de regalo que el cliente lleva SIN la propiedad: el descuento automatico se las aplica primero a ellas.
 * La BASE es la suma de `original_line_price` de todo lo que NO es gomita de regalo (ni la linea de regalo ni las
 * unidades propias de esa variante: el descuento de la tienda no las cuenta para alcanzar sus montos). La linea de regalo es
 * la que lleva la variante de regalo Y la propiedad oculta `_thcc_regalo` = `1`. El precio $0 lo pone el descuento
 * automatico de la tienda (esto solo agrega y quita la linea); las gomitas CBD que el cliente compra por su cuenta
 * (sin la propiedad) no suman a la base, pero si cuentan para la cantidad de regalo (ver abajo).
 *
 * Variante, existencias, montos y cantidades NO estan aqui: los entrega el servidor en
 * <script type="application/json" data-thcc-regalo-config> (snippets/thcc-cart-regalo-config.liquid).
 *
 * Idempotente y sin bucles: cada pasada lee /cart.js, compara con lo deseado y, solo si difiere, hace UNA escritura
 * (`/cart/update.js` por clave de linea, o `/cart/add.js` si la linea no existe) pidiendo en la misma llamada las
 * secciones que repinta el carrito (las de cada <cart-items-component>). Despues despacha un
 * StandardEvents.cartLinesUpdate con esas secciones para que Horizon repinte cajon / pagina y el contador de la
 * cabecera y de la barra inferior (cart-icon.js, thcc-cabecera.js, thcc-nav-barra.js) queden al dia. Ese evento
 * propio se ignora a si mismo. Candado: una pasada a la vez (bandera + Web Locks entre pestanas); si llegan
 * eventos durante una pasada se anota uno pendiente y se repite, con tope de vueltas. Si una escritura falla
 * (agotado, red) se avisa UNA vez con console.warn y no se reintenta el mismo estado.
 *
 * Sin JavaScript el carrito sigue funcionando; solo no se agrega el regalo.
 */

const PROPIEDAD = '_thcc_regalo';
const CANDADO = 'thcc-cart-regalo';
const MAX_VUELTAS = 3;
const MAX_ESCRITURAS = 6;
const VENTANA_MS = 10000;

/** @type {any} */
let eventos = null;
/** @type {any} */
let configCache = null;
let corriendo = false;
let pendiente = false;
/** @type {AbortController | null} */
let lectura = null;
/** @type {number[]} */
let escrituras = [];
const fallos = new Set();
const avisos = new Set();
/** @type {WeakSet<object>} */
const propios = new WeakSet();

/**
 * @param {string} clave
 * @param {...any} datos
 */
function avisarUnaVez(clave, ...datos) {
  if (avisos.has(clave)) return;
  avisos.add(clave);
  console.warn('[thcc-cart-regalo]', ...datos);
}

/** @returns {any} */
function leerConfig() {
  const el = document.querySelector('script[data-thcc-regalo-config]');
  if (el) {
    try {
      const datos = JSON.parse(el.textContent || '{}');
      if (datos && typeof datos === 'object') {
        configCache = {
          variantId: Number(datos.variantId) || 0,
          available: datos.available === true,
          metaUno: Number(datos.metaUno) || 0,
          metaDos: Number(datos.metaDos) || 0,
          cantidadUno: Math.max(0, Math.floor(Number(datos.cantidadUno) || 0)),
          cantidadDos: Math.max(0, Math.floor(Number(datos.cantidadDos) || 0)),
        };
      }
    } catch (_) {
      // JSON invalido: se conserva la ultima configuracion buena.
    }
  }
  return configCache;
}

/** @param {string} ruta */
function url(ruta) {
  // @ts-ignore
  const raiz = typeof window.Shopify?.routes?.root === 'string' ? window.Shopify.routes.root : '/';
  return `${raiz.replace(/\/$/, '')}${ruta}`;
}

/** @param {any} linea */
function esLineaRegalo(linea) {
  return linea?.properties?.[PROPIEDAD] === '1';
}

/** @returns {string[]} */
function seccionesDelCarrito() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * @param {string} ruta
 * @param {any} cuerpo
 */
async function escribir(ruta, cuerpo) {
  const respuesta = await fetch(url(ruta), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  /** @type {any} */
  let datos = null;
  try {
    datos = await respuesta.json();
  } catch (_) {
    datos = null;
  }
  if (!respuesta.ok || datos?.status || datos?.errors) {
    throw new Error(datos?.description || datos?.message || `HTTP ${respuesta.status}`);
  }
  return datos;
}

async function leerCarrito() {
  lectura?.abort();
  const control = new AbortController();
  lectura = control;
  try {
    const respuesta = await fetch(url('/cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal: control.signal,
    });
    if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
    return await respuesta.json();
  } finally {
    if (lectura === control) lectura = null;
  }
}

/**
 * Avisa a Horizon (y a los contadores) del cambio que hizo este modulo.
 * @param {any} carrito - Respuesta de /cart.js tras la escritura
 * @param {Record<string, string> | undefined} secciones
 */
function anunciar(carrito, secciones) {
  if (!eventos) return;
  try {
    const { CartLinesUpdateEvent } = eventos;
    const regalos = (carrito.items || []).filter(esLineaRegalo);
    const linea = regalos[0];
    const accion = linea ? 'update' : 'remove';
    const { promise, resolve } = CartLinesUpdateEvent.createPromise();
    resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
      detail: {
        sections: secciones,
        items: carrito.items,
        itemCount: carrito.item_count,
        source: 'thcc-cart-regalo',
        didError: false,
      },
    });
    const evento = new CartLinesUpdateEvent({
      action: accion,
      context: 'cart',
      lines: [{ id: String(linea?.key ?? 'thcc-regalo'), quantity: linea?.quantity ?? 0 }],
      promise,
    });
    propios.add(evento);
    document.dispatchEvent(evento);
  } catch (error) {
    avisarUnaVez('anunciar', 'no se pudo avisar al carrito del cambio', error);
  }
}

function hayTopeDeEscrituras() {
  const ahora = Date.now();
  escrituras = escrituras.filter((t) => ahora - t < VENTANA_MS);
  if (escrituras.length >= MAX_ESCRITURAS) {
    avisarUnaVez('tope', 'demasiadas escrituras seguidas; se detiene hasta recargar');
    return true;
  }
  return false;
}

/**
 * Una pasada: lee, compara y, si hace falta, escribe una vez.
 * @returns {Promise<void>}
 */
async function pasada() {
  const config = leerConfig();
  if (!config) return;

  /** @type {any} */
  let carrito;
  try {
    carrito = await leerCarrito();
  } catch (error) {
    if (/** @type {any} */ (error)?.name === 'AbortError') return;
    avisarUnaVez('leer', 'no se pudo leer el carrito', error);
    return;
  }

  const lineas = Array.isArray(carrito?.items) ? carrito.items : [];

  // Renglones duplicados: cuando el regalo se activa, Shopify marca la linea que califica con el descuento y una
  // nueva adicion del mismo producto ya no se fusiona con ella (queda "Cherry Fuel x4" + "Cherry Fuel x3"). Se
  // juntan en la primera linea (misma variante y mismas propiedades) y la pasada se repite para ajustar el regalo.
  /** @type {Map<string, any[]>} */
  const grupos = new Map();
  for (const l of lineas) {
    if (esLineaRegalo(l)) continue;
    const clave = `${l.variant_id}|${JSON.stringify(l.properties || {})}|${l.selling_plan_allocation?.selling_plan?.id || ''}`;
    const grupo = grupos.get(clave) || [];
    grupo.push(l);
    grupos.set(clave, grupo);
  }
  /** @type {Record<string, number>} */
  const fusion = {};
  for (const grupo of grupos.values()) {
    if (grupo.length < 2) continue;
    fusion[grupo[0].key] = grupo.reduce((/** @type {number} */ s, /** @type {any} */ l) => s + (Number(l.quantity) || 0), 0);
    for (const l of grupo.slice(1)) fusion[l.key] = 0;
  }
  if (Object.keys(fusion).length > 0 && !hayTopeDeEscrituras()) {
    escrituras.push(Date.now());
    const secciones = seccionesDelCarrito();
    const extra = secciones.length ? { sections: secciones.join(','), sections_url: window.location.pathname } : {};
    try {
      const respuesta = await escribir('/cart/update.js', { updates: fusion, ...extra });
      if (respuesta) anunciar(respuesta, respuesta.sections);
      pendiente = true;
    } catch (error) {
      avisarUnaVez('fusion', 'no se pudieron juntar renglones duplicados', error);
    }
    return;
  }

  const propias = lineas.filter(esLineaRegalo);
  const correctas = propias.filter((/** @type {any} */ l) => config.variantId && l.variant_id === config.variantId);
  const sobrantes = propias.filter((/** @type {any} */ l) => !correctas.includes(l));
  const base = lineas
    .filter((/** @type {any} */ l) => !esLineaRegalo(l) && !(config.variantId && l.variant_id === config.variantId))
    .reduce((/** @type {number} */ suma, /** @type {any} */ l) => suma + (Number(l.original_line_price) || 0), 0);

  let deseado = 0;
  if (base > 0 && config.variantId) {
    if (config.metaDos > 0 && base >= config.metaDos * 100) deseado = config.cantidadDos;
    else if (config.metaUno > 0 && base >= config.metaUno * 100) deseado = config.cantidadUno;
  }
  // Shopify aplica el descuento primero a la gomita que el cliente ya lleva por su cuenta (sin la propiedad): esa
  // ES su regalo. Por eso la linea de regalo solo cubre lo que falta: max(0, regalos deseados - gomitas propias).
  const propiasGomita = lineas
    .filter((/** @type {any} */ l) => !esLineaRegalo(l) && config.variantId && l.variant_id === config.variantId)
    .reduce((/** @type {number} */ s, /** @type {any} */ l) => s + (Number(l.quantity) || 0), 0);
  deseado = Math.max(0, deseado - propiasGomita);
  const actual = correctas.reduce((/** @type {number} */ s, /** @type {any} */ l) => s + l.quantity, 0);
  // Agotada: solo se puede conservar o quitar lo que ya hay, no subir.
  if (!config.available && deseado > actual) deseado = actual;

  const necesitaAgregar = deseado > 0 && correctas.length === 0;
  const hayQueAjustar =
    sobrantes.length > 0 ||
    correctas.length > 1 ||
    (correctas.length === 1 && correctas[0].quantity !== deseado) ||
    necesitaAgregar;
  if (!hayQueAjustar) return;

  const firma = `${config.variantId}|${deseado}|${actual}|${propias.length}`;
  if (fallos.has(firma)) return;
  if (hayTopeDeEscrituras()) return;
  escrituras.push(Date.now());

  const secciones = seccionesDelCarrito();
  const extra = secciones.length
    ? { sections: secciones.join(','), sections_url: window.location.pathname }
    : {};

  try {
    /** @type {any} */
    let respuesta = null;
    /** @type {Record<string, number>} */
    const cambios = {};
    for (const l of sobrantes) cambios[l.key] = 0;
    if (correctas.length > 0) {
      correctas.forEach((/** @type {any} */ l, /** @type {number} */ i) => {
        cambios[l.key] = i === 0 ? deseado : 0;
      });
    }
    const hayCambios = Object.keys(cambios).length > 0;
    const hayQueAgregar = necesitaAgregar;

    if (hayCambios) {
      respuesta = await escribir('/cart/update.js', { updates: cambios, ...(hayQueAgregar ? {} : extra) });
    }
    /** @type {any} */
    let carritoFinal = respuesta;
    let seccionesFinal = respuesta?.sections;
    if (hayQueAgregar) {
      const agregado = await escribir('/cart/add.js', {
        items: [{ id: config.variantId, quantity: deseado, properties: { [PROPIEDAD]: '1' } }],
        ...extra,
      });
      seccionesFinal = agregado?.sections;
      carritoFinal = await leerCarrito();
    }
    if (carritoFinal) anunciar(carritoFinal, seccionesFinal);
  } catch (error) {
    fallos.add(firma);
    avisarUnaVez(`escribir:${firma}`, 'no se pudo ajustar el regalo; el carrito queda como esta', error);
  }
}

/** @param {() => Promise<void>} tarea */
function conCandado(tarea) {
  const locks = /** @type {any} */ (navigator).locks;
  if (locks && typeof locks.request === 'function') return locks.request(CANDADO, tarea);
  return tarea();
}

async function reconciliar() {
  if (corriendo) {
    pendiente = true;
    // La lectura en curso puede estar vieja: se cancela y la pasada se repite con el estado nuevo.
    lectura?.abort();
    return;
  }
  corriendo = true;
  try {
    let vueltas = 0;
    do {
      pendiente = false;
      vueltas += 1;
      await conCandado(pasada);
    } while (pendiente && vueltas < MAX_VUELTAS);
  } catch (error) {
    avisarUnaVez('reconciliar', 'error inesperado', error);
  } finally {
    corriendo = false;
  }
}

/** @param {any} evento */
function alCambiarElCarrito(evento) {
  if (propios.has(evento)) return;
  const promesa = evento?.promise;
  if (promesa && typeof promesa.then === 'function') {
    promesa.then(
      () => reconciliar(),
      () => {
        // Cambio fallido (p. ej. sin existencias): no hay nada nuevo que reconciliar.
      }
    );
  } else {
    reconciliar();
  }
}

async function iniciar() {
  if (!document.querySelector('script[data-thcc-regalo-config]')) return;

  try {
    eventos = await import('@shopify/events');
    document.addEventListener(eventos.StandardEvents.cartLinesUpdate, alCambiarElCarrito);
  } catch (_) {
    // Sin el modulo de eventos no hay repintado coordinado: solo la pasada de carga (y el carrito se actualiza al recargar).
  }

  window.addEventListener('pageshow', (evento) => {
    if (/** @type {PageTransitionEvent} */ (evento).persisted) reconciliar();
  });

  reconciliar();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', iniciar, { once: true });
} else {
  iniciar();
}
